import { Temporal } from "@js-temporal/polyfill";

const DATE_REGEX = /^(\d{4})-(\d{2})-(\d{2})$/;
const MAX_YEAR_OFFSET = 4;
const PAST_WINDOW_DAYS = 90;
const PRIOR_YEAR_PAST_WINDOW_DAYS = 35;

interface NormalizeInput {
  startDate: string;
  endDate: string;
  hasExplicitYear: boolean;
}

interface NormalizeOutput {
  startDate: string;
  endDate: string;
}

function parseYMD(value: string): { year: number; month: number; day: number } {
  const match = DATE_REGEX.exec(value);
  if (!match) throw new Error(`Expected YYYY-MM-DD, got: ${value}`);
  return {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3]),
  };
}

function findDate(
  startYear: number,
  month: number,
  day: number,
  notBefore?: Temporal.PlainDate,
): Temporal.PlainDate {
  for (let offset = 0; offset <= MAX_YEAR_OFFSET; offset++) {
    try {
      const candidate = Temporal.PlainDate.from(
        { year: startYear + offset, month, day },
        { overflow: "reject" },
      );
      if (!notBefore || Temporal.PlainDate.compare(candidate, notBefore) >= 0) {
        return candidate;
      }
    } catch {}
  }
  throw new Error(
    `Could not find a valid date for month ${month}, day ${day} ` +
      `within ${MAX_YEAR_OFFSET} years of ${startYear}` +
      (notBefore ? ` on or after ${notBefore.toString()}` : ""),
  );
}

function findStartDate(
  month: number,
  day: number,
  today: Temporal.PlainDate,
): Temporal.PlainDate {
  const earliestSameYear = today.subtract({ days: PAST_WINDOW_DAYS });
  const earliestPriorYear = today.subtract({
    days: PRIOR_YEAR_PAST_WINDOW_DAYS,
  });
  for (let offset = 0; offset <= MAX_YEAR_OFFSET; offset++) {
    try {
      const candidate = Temporal.PlainDate.from(
        { year: earliestSameYear.year + offset, month, day },
        { overflow: "reject" },
      );
      const earliest =
        candidate.year < today.year ? earliestPriorYear : earliestSameYear;
      if (Temporal.PlainDate.compare(candidate, earliest) >= 0) {
        return candidate;
      }
    } catch {}
  }
  throw new Error(
    `Could not find a valid start date for ${month}-${day} ` +
      `within ${MAX_YEAR_OFFSET} years of ${earliestSameYear.year}`,
  );
}

export function normalizeEventYear(
  input: NormalizeInput,
  today: Temporal.PlainDate,
): NormalizeOutput {
  const start = parseYMD(input.startDate);
  const end = parseYMD(input.endDate);

  const startDate = input.hasExplicitYear
    ? Temporal.PlainDate.from(
        { year: start.year, month: start.month, day: start.day },
        { overflow: "reject" },
      )
    : findStartDate(start.month, start.day, today);

  const endStartYear = input.hasExplicitYear
    ? Math.max(end.year, startDate.year)
    : startDate.year;
  const endDate = findDate(endStartYear, end.month, end.day, startDate);

  return {
    startDate: startDate.toString(),
    endDate: endDate.toString(),
  };
}
